export const DEFAULT_CHESS_MODEL_PARAMETERS = Object.freeze({
  baseDrawProbability: 0.09,
  referenceElo: 1800,
  eloStep: 200,
  eloRelativeEffect: 0.444,
  referenceMinutes: 5,
  timeRelativeEffect: 0.38,
  defaultPlayerDrawFactor: 1,
  whiteAdvantage: 0.52,
  eloScale: 400,
});

export const LEGACY_EXCEL_CHESS_MODEL_PARAMETERS = Object.freeze({
  whiteAdvantage: 0.52,
  eloScale: 400,
  drawEloInfluence: 0.0004,
  drawEloConstant: 0.1,
  drawTimeInfluence: 0.0067,
  drawTimeConstant: 0.1,
  drawDivisor: 3,
  defaultPlayerDrawCoefficient: 0.5,
});

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));
const numberOr = (value, fallback) => {
  if (value === "" || value === null || value === undefined) return fallback;
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};
const positiveNumberOr = (value, fallback) => {
  const number = numberOr(value, fallback);
  return number > 0 ? number : fallback;
};
const eloExpectation = (elo1, elo2, scale) => 1 / (1 + 10 ** ((elo2 - elo1) / scale));
const applyWhiteAdvantage = (expectation, player1Color, whiteAdvantage) => {
  const adjustment = positiveNumberOr(whiteAdvantage, 0.5) / 0.5;
  if (player1Color === "white") return clamp(expectation * adjustment, 0, 1);
  if (player1Color === "black") return clamp(expectation / adjustment, 0, 1);
  return expectation;
};

export function calculateEqualStrengthDrawProbability({
  averageElo,
  effectiveMinutes,
  player1DrawFactor,
  player2DrawFactor,
  parameters = DEFAULT_CHESS_MODEL_PARAMETERS,
}) {
  const model = { ...DEFAULT_CHESS_MODEL_PARAMETERS, ...parameters };
  const referenceElo = numberOr(model.referenceElo, 1800);
  const eloStep = positiveNumberOr(model.eloStep, 200);
  const referenceMinutes = positiveNumberOr(model.referenceMinutes, 5);
  const minutes = positiveNumberOr(effectiveMinutes, referenceMinutes);
  const eloSteps = (numberOr(averageElo, referenceElo) - referenceElo) / eloStep;
  const eloMultiplier = Math.max(0, 1 + numberOr(model.eloRelativeEffect, 0) * eloSteps);
  const timeDoublings = Math.log2(minutes / referenceMinutes);
  const timeMultiplier = Math.max(0, 1 + numberOr(model.timeRelativeEffect, 0) * timeDoublings);
  const defaultFactor = numberOr(model.defaultPlayerDrawFactor, 1);
  const individualFactor = Math.max(0, (
    numberOr(player1DrawFactor, defaultFactor) + numberOr(player2DrawFactor, defaultFactor)
  ) / 2);

  return clamp(
    numberOr(model.baseDrawProbability, 0) * eloMultiplier * timeMultiplier * individualFactor,
    0,
    1,
  );
}

export function calculateChessProbabilities({
  player1Elo,
  player2Elo,
  player1Color = "white",
  effectiveMinutes,
  player1DrawFactor,
  player2DrawFactor,
  parameters = DEFAULT_CHESS_MODEL_PARAMETERS,
}) {
  const model = { ...DEFAULT_CHESS_MODEL_PARAMETERS, ...parameters };
  const elo1 = numberOr(player1Elo, model.referenceElo);
  const elo2 = numberOr(player2Elo, model.referenceElo);
  const expectation = eloExpectation(elo1, elo2, positiveNumberOr(model.eloScale, 400));
  const adjustedExpectation = applyWhiteAdvantage(expectation, player1Color, model.whiteAdvantage);
  const equalStrengthDrawProbability = calculateEqualStrengthDrawProbability({
    averageElo: (elo1 + elo2) / 2,
    effectiveMinutes,
    player1DrawFactor,
    player2DrawFactor,
    parameters: model,
  });
  const differenceFactor = 2 * Math.min(expectation, 1 - expectation);
  const unconstrainedDraw = equalStrengthDrawProbability * differenceFactor;
  const maximumPossibleDraw = 2 * Math.min(adjustedExpectation, 1 - adjustedExpectation);
  const draw = clamp(unconstrainedDraw, 0, maximumPossibleDraw);
  const player1Win = adjustedExpectation - draw / 2;
  const player2Win = 1 - adjustedExpectation - draw / 2;

  return { player1Win, draw, player2Win, expectation, adjustedExpectation, equalStrengthDrawProbability, differenceFactor };
}

export function calculateLegacyExcelChessProbabilities({
  player1Elo,
  player2Elo,
  player1Color = "white",
  effectiveMinutes,
  player1DrawCoefficient,
  player2DrawCoefficient,
  parameters = LEGACY_EXCEL_CHESS_MODEL_PARAMETERS,
}) {
  const model = { ...LEGACY_EXCEL_CHESS_MODEL_PARAMETERS, ...parameters };
  const elo1 = numberOr(player1Elo, 0);
  const elo2 = numberOr(player2Elo, 0);
  const expectation = eloExpectation(elo1, elo2, positiveNumberOr(model.eloScale, 400));
  const adjustedExpectation = applyWhiteAdvantage(expectation, player1Color, model.whiteAdvantage);
  const coefficient1 = numberOr(player1DrawCoefficient, model.defaultPlayerDrawCoefficient);
  const coefficient2 = numberOr(player2DrawCoefficient, model.defaultPlayerDrawCoefficient);
  const drawDistribution = (
    ((elo1 + elo2) / 2) * numberOr(model.drawEloInfluence, 0) +
    numberOr(model.drawEloConstant, 0) +
    numberOr(effectiveMinutes, 0) * numberOr(model.drawTimeInfluence, 0) +
    numberOr(model.drawTimeConstant, 0) +
    (coefficient1 + coefficient2) / 2
  ) / positiveNumberOr(model.drawDivisor, 1);
  const draw = 2 * drawDistribution * Math.min(1 - adjustedExpectation, adjustedExpectation);
  const player1Win = adjustedExpectation - draw / 2;
  return { player1Win, draw, player2Win: 1 - player1Win - draw, expectation, adjustedExpectation, drawDistribution };
}
